import { Eye, Pencil, /* Power, */ Mail, Phone, MapPin, CreditCard, CheckCircle, Clock, User } from "lucide-react";

const getStatusBadge = (client) => {
  const status = client.status?.toLowerCase() || "inactive";
  if (status === "active") {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
        <CheckCircle className="mr-1 w-3 h-3" />
        Active
      </span>
    );
  } else if (status === "pending") {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full">
        <Clock className="mr-1 w-3 h-3" />
        Pending
      </span>
    );
  } else {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
        <Clock className="mr-1 w-3 h-3" />
        Inactive
      </span>
    );
  }
};

const ClientCard = ({ clients, onView, onEdit /* onActivate */ }) => {
  if (clients.length === 0) return null;

  return (
    <div className="divide-y divide-gray-200 lg:hidden">
      {clients.map((client) => (
        <div
          key={client.slug || client.id}
          className="p-4 transition-colors hover:bg-gray-50"
        >
          <div className="flex justify-between items-start mb-3">
            <div className="flex flex-1 items-center min-w-0">
              <div className="flex flex-shrink-0 justify-center items-center mr-3 w-10 h-10 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full shadow-sm">
                <span className="text-sm font-medium text-white">
                  {client.firstName?.charAt(0) || "C"}
                  {client.lastName?.charAt(0) || "L"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-gray-900 truncate">
                  {client.firstName} {client.lastName}
                </div>
                <div className="text-sm text-gray-500 truncate">
                  {client.documentType || "No Document"}
                </div>
                <div className="mt-1">{getStatusBadge(client)}</div>
              </div>
            </div>
            <div className="flex ml-2 space-x-1">
              <button
                onClick={() => onView(client)}
                className="p-2 text-gray-400 rounded-lg transition-colors hover:text-emerald-600 hover:bg-emerald-50 touch-manipulation"
                title="View Details"
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                onClick={() => onEdit(client)}
                className="p-2 text-gray-400 rounded-lg transition-colors hover:text-blue-600 hover:bg-blue-50 touch-manipulation"
                title="Edit Client"
              >
                <Pencil className="w-4 h-4" />
              </button>
              {/* Activation button - COMMENTED OUT */}
              {/* <button
                onClick={() => onActivate(client)}
                className={`p-2 rounded-lg transition-colors touch-manipulation ${
                  client.status?.toLowerCase() === "active"
                    ? "text-red-500 hover:text-red-600 hover:bg-red-50"
                    : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                }`}
                title={
                  client.status?.toLowerCase() === "active"
                    ? "Deactivate Client"
                    : "Activate Client"
                }
              >
                <Power className="w-4 h-4" />
              </button> */}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {/* Contact Info */}
            <div className="space-y-2">
              {client.email && (
                <div className="flex items-center text-sm">
                  <Mail className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                  <span className="text-gray-900 truncate">{client.email}</span>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center text-sm">
                  <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                  <span className="text-gray-900 truncate">{client.phone}</span>
                </div>
              )}
            </div>

            {/* Location */}
            {(client.location || client.region) && (
              <div className="space-y-2">
                {client.location && (
                  <div className="flex items-center text-sm">
                    <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 truncate">
                      {client.location}
                    </span>
                  </div>
                )}
                {client.region && (
                  <div className="ml-6 text-sm text-gray-500 truncate">
                    {client.region}
                  </div>
                )}
              </div>
            )}

            {/* Document Info */}
            {client.cardNumber && (
              <div className="pt-2 border-t border-gray-100">
                <div className="text-sm text-gray-600">
                  <CreditCard className="inline mr-1 w-4 h-4" />
                  {client.documentType}:{" "}
                  <span className="font-mono">{client.cardNumber}</span>
                </div>
              </div>
            )}

            {/* Agent Info */}
            {client.agent && (
              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center mb-1 text-xs font-medium text-gray-500 uppercase">
                  <User className="mr-1 w-3 h-3" />
                  Registering Agent
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {client.agentName || "N/A"}
                </div>
                {client.agentCode && (
                  <div className="mt-1 text-xs text-emerald-700">
                    Code: <span className="font-mono">{client.agentCode}</span>
                  </div>
                )}
                {client.agentEmail && (
                  <div className="mt-1 text-xs text-gray-600 truncate">
                    {client.agentEmail}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ClientCard;
